import React from 'react';
import { graphql, useFragment } from 'react-relay';
import { CheckboxSelectInlineEditViewWithIsEditable } from '@atlassian/jira-issue-field-checkbox-select-inline-edit-full/src/ui/checkbox-select/index.tsx';
import type { checkboxSelect_nativeIssueTable_CheckboxSelectCell$key } from '@atlassian/jira-relay/src/__generated__/checkboxSelect_nativeIssueTable_CheckboxSelectCell.graphql';
import { INLINE_EDIT_ANALYTICS_ATTRIBUTES } from '../common/inline-edit-analytics-attributes.tsx';
import { useLazyLoadEditability } from '../../../../services/lazy-editability-loader/index.tsx';
import {
	useIsInlineEditingExtendedFieldSupportEnabled,
	useIsSingleLineRowHeightEnabled,
} from '../../../../controllers/features/selectors.tsx';

export type Props = {
	fieldRef: checkboxSelect_nativeIssueTable_CheckboxSelectCell$key;
	isReadOnlyWithoutHoverPopover?: boolean;
};

export const CheckboxSelectCell = ({ fieldRef, isReadOnlyWithoutHoverPopover }: Props) => {
	const data = useFragment<checkboxSelect_nativeIssueTable_CheckboxSelectCell$key>(
		graphql`
			fragment checkboxSelect_nativeIssueTable_CheckboxSelectCell on JiraCheckboxesField
			@argumentDefinitions(isInlineEditingEnabled: { type: "Boolean!" }) {
				id
				lazyIsEditableInIssueView @include(if: $isInlineEditingEnabled)
				...checkboxSelect_issueFieldCheckboxSelectInlineEditFull_CheckboxSelectInlineEditViewWithIsEditable_fragmentRef
			}
		`,
		fieldRef,
	);

	const isInlineEditingExtendedFieldSupportEnabled =
		useIsInlineEditingExtendedFieldSupportEnabled();
	const isSingleLineRowHeightEnabled = useIsSingleLineRowHeightEnabled();
	// If the property does not exist in fragment data then we know it has been conditionally excluded because inline
	// editing is disabled. In this case we explicitly set isEditable to false to bypass the lazy editability query.
	const isEditable =
		'lazyIsEditableInIssueView' in data && isInlineEditingExtendedFieldSupportEnabled
			? data.lazyIsEditableInIssueView
			: false;
	useLazyLoadEditability(data.id, isEditable);

	return (
		<CheckboxSelectInlineEditViewWithIsEditable
			attributes={INLINE_EDIT_ANALYTICS_ATTRIBUTES}
			spacing="default"
			editViewPopup
			editViewPopupAlignBlock="center"
			fragmentRef={data}
			isEditable={(isEditable && !isReadOnlyWithoutHoverPopover) ?? false}
			menuPosition="fixed"
			readViewFitContainerHeight
			isTruncated
			isSingleLineRowHeightEnabled={isSingleLineRowHeightEnabled && !isReadOnlyWithoutHoverPopover}
		/>
	);
};
